import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, switchMap, take } from 'rxjs';
import { BaseUrlInterceptorService } from '../../services/base-url-interceptor.service';
import { ProfileDataService } from '../../services/data/profile-data.service';
import { SessionCheckerService } from '../../services/session-checker.service';
import { LocalStorageService } from '../../services/storage/local-storage.service';
import { UuidService } from '../../services/uuid.service';
import { AuthFacade } from '../auth.facade';
import { AppConfigService } from 'src/app/services/app-config.service';

/**
 * HTTP interceptor that:
 * 1. Blocks requests when the user is not authenticated.
 * 2. Injects `Authorization:Bearer <token>` + contextual headers
 * (`X-Trace-Id`, `X-User-*`).
 * 3. Rewrites placeholder URLs (`https://wallet-service/...`) to the actual URLs of the microservices via `BaseUrlInterceptorService`.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const appConfigService = inject(AppConfigService);
  const authFacade = inject(AuthFacade);
  const baseUrlService = inject(BaseUrlInterceptorService);
  const localStorageService = inject(LocalStorageService);
  const profileDataService = inject(ProfileDataService);
  const router = inject(Router);
  const sessionChecker = inject(SessionCheckerService);
  const uuidService = inject(UuidService);

  const bypassIssuerHostnames = [
    appConfigService.config.oidc?.issuer,
    appConfigService.config.okta?.issuer,
  ]
    .filter(Boolean)
    .flatMap((issuer) => {
      try {
        return [new URL(issuer).hostname];
      } catch {
        return [];
      }
    });

  let reqHostname: string;
  try {
    reqHostname = new URL(req.url).hostname;
  } catch {
    reqHostname = '';
  }

  // Full bypass for IdP authorization and token endpoints
  if (reqHostname && bypassIssuerHostnames.includes(reqHostname)) {
    return next(req);
  }

  if (!authFacade.isAuthenticated()) {
    router.navigateByUrl('/auth-error');
    return EMPTY;
  }

  return sessionChecker.isSessionActive().pipe(
    take(1),
    switchMap((isActive) => {
      if (!isActive) {
        router.navigateByUrl('/auth-error');
        return EMPTY;
      }
      return authFacade.getAccessToken().pipe(
        take(1),
        switchMap((accessToken) => {
          const headers = buildHeaders({
            accessToken,
            localStorageService,
            profileDataService,
            uuidService,
          });
          const url = baseUrlService.verifyUrl(req.url);
          return next(req.clone({ headers, url }));
        }),
      );
    }),
  );
};

interface BuildHeadersDeps {
  accessToken: string;
  localStorageService: LocalStorageService;
  profileDataService: ProfileDataService;
  uuidService: UuidService;
}

function buildHeaders({
  accessToken,
  localStorageService,
  profileDataService,
  uuidService,
}: BuildHeadersDeps): HttpHeaders {
  let headers = new HttpHeaders().set(
    'Accept',
    'application/json,application/octet-stream,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf',
  );

  if (accessToken) {
    headers = headers.set('Authorization', `Bearer ${accessToken}`);
  }

  headers = headers.set('X-Trace-Id', uuidService.generateV4());

  const { personType, accountId, environment, userCountry } = localStorageService.getAllItems();

  if (environment) {
    headers = headers.set('X-User-Environment', environment.toLowerCase());
  }
  if (userCountry) {
    headers = headers.set('X-User-Country', userCountry.toLowerCase());
  }
  if (personType) {
    headers = headers.set('X-User-Registry-Type', personType);
  }
  if (accountId) {
    headers = headers.set('X-User-Account-Id', accountId);
  }

  const currentProfile = profileDataService.currentProfile;
  if (currentProfile?.userId) {
    headers = headers.set('X-User-Id', currentProfile.userId);
  }
  if (currentProfile?.planId) {
    headers = headers.set('X-User-Plan-Id', currentProfile.planId);
  }
  if (currentProfile?.clientId) {
    headers = headers.set('X-User-Client-Id', currentProfile.clientId);
  }

  return headers;
}
